import { CameraAlt, CheckCircle } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
  ExceptionalPartCreateStepProps,
  ExceptionalPartCreateStepRef,
  ExceptionalPartFormData,
} from './ExceptionalPartCreateStep.props';
import {
  ErrorMessage,
  ExceptionalImageCapture,
  ExceptionalImageCaptureRow,
  ExceptionalMessage,
  ExceptionalSection,
} from './MobilePartActionsDialog.styles';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../../@types/partType.type';
import { useSearchPartsOrPartTypes } from '../../../../../../queries/live-search-query';

interface ImageCaptureButtonProps {
  file: File | undefined;
  label: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (f: File) => void;
}

const ImageCaptureButton: React.FC<ImageCaptureButtonProps> = ({
  file,
  label,
  inputRef,
  onChange,
}) => (
  <ExceptionalImageCapture>
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={(e) => e.target.files?.[0] && onChange(e.target.files[0])}
    />
    <Box
      component="button"
      onClick={() => inputRef.current?.click()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        padding: '12px 8px',
        border: '1px dashed',
        borderColor: file ? 'success.main' : 'divider',
        borderRadius: 2,
        background: 'none',
        cursor: 'pointer',
        color: file ? 'success.main' : 'text.secondary',
      }}
    >
      {file ? <CheckCircle /> : <CameraAlt />}
      <Typography variant="caption" textAlign="center">
        {file ? file.name : label}
      </Typography>
    </Box>
  </ExceptionalImageCapture>
);

const ExceptionalPartCreateStep = forwardRef<
  ExceptionalPartCreateStepRef,
  ExceptionalPartCreateStepProps
>(({ actionFor, service, isSubmitting, error, initialSerial, onSubmit, onValidChange }, ref) => {
  const [allPartTypes, setAllPartTypes] = useState<PartTypeActionLiveSearchItem[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<PartTypeActionLiveSearchItem[]>([]);
  const [partTypeInput, setPartTypeInput] = useState('');
  const [selectedPartType, setSelectedPartType] = useState<PartTypeActionLiveSearchItem | null>(
    null
  );
  const [serial, setSerial] = useState(initialSerial ?? '');
  const [deviceImage, setDeviceImage] = useState<File | undefined>();
  const [labelImage, setLabelImage] = useState<File | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);

  const deviceInputRef = useRef<HTMLInputElement>(null);
  const labelInputRef = useRef<HTMLInputElement>(null);
  const searchMutation = useSearchPartsOrPartTypes();

  const isValid = !!selectedPartType && !!serial.trim();

  useEffect(() => {
    const loadPartTypes = async (): Promise<void> => {
      setIsLoadingTypes(true);
      try {
        const results = await searchMutation.mutateAsync({
          name: '',
          serialiseType: PartTypeActionLiveSearchItemCategoryCode.Serialised,
          partTypeId: '',
          clientContractIds: [service.clientId || 0, service.contractId || 0],
          locationId: service.mainLocationNodeId || 0,
          allowedPartLocationType: 'node',
          partActionType: actionFor === 'in' ? 'install' : 'remove',
          searchType: 'part-type',
          serviceTypeCode: service.serviceTypeCode || '',
          preFill: true,
        });
        const unique = [...new Map(results.map((r) => [r.partTypeId, r])).values()];
        setAllPartTypes(unique);
        setFilteredOptions(unique);
      } catch {
        setAllPartTypes([]);
        setFilteredOptions([]);
      } finally {
        setIsLoadingTypes(false);
      }
    };
    loadPartTypes();
  }, [
    service.clientId,
    service.contractId,
    service.mainLocationNodeId,
    service.serviceTypeCode,
    actionFor,
  ]);

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  const handleSubmit = async (): Promise<void> => {
    if (!selectedPartType) {
      setFormError('Please select a part type');
      return;
    }
    if (!serial.trim()) {
      setFormError('Please enter the serial number');
      return;
    }
    setFormError(null);
    const data: ExceptionalPartFormData = {
      partTypeId: selectedPartType.partTypeId,
      partTypeName: selectedPartType.partTypeName,
      serial1: serial.trim(),
      deviceImageFile: deviceImage,
      labelImageFile: labelImage,
    };
    await onSubmit(data);
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const handleInputChange = (value: string): void => {
    setPartTypeInput(value);
    if (!value.trim()) {
      setFilteredOptions(allPartTypes);
      return;
    }
    const lower = value.toLowerCase();
    setFilteredOptions(allPartTypes.filter((pt) => pt.partTypeName.toLowerCase().includes(lower)));
  };

  const contextMessage =
    actionFor === 'in'
      ? 'This will create the part for usage in this service. The part will be reported to the client as delivered. Ensure the information is correct.'
      : 'This will create the part for inventory. Ensure the information is correct — the part must be returned promptly for review and acceptance.';

  return (
    <ExceptionalSection>
      {(error || formError) && <ErrorMessage>{error || formError}</ErrorMessage>}
      <ExceptionalMessage>{contextMessage}</ExceptionalMessage>

      <TextField
        label="Serial Number *"
        value={serial}
        onChange={(e) => setSerial(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        disabled={isSubmitting}
      />

      <Autocomplete
        options={filteredOptions}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.partTypeName)}
        inputValue={partTypeInput}
        value={selectedPartType}
        loading={isLoadingTypes}
        filterOptions={(opts) => opts}
        onInputChange={(_, value) => handleInputChange(value)}
        onChange={(_, value) => {
          setSelectedPartType(value);
          if (value) setPartTypeInput(value.partTypeName);
        }}
        sx={{ mb: 2 }}
        componentsProps={{
          popper: { placement: 'top-start', modifiers: [{ name: 'flip', enabled: false }] },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Part Type *"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingTypes && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={isLoadingTypes ? 'Loading part types...' : 'No matching part types'}
      />

      <ExceptionalImageCaptureRow>
        <ImageCaptureButton
          file={deviceImage}
          label="Device Photo (optional)"
          inputRef={deviceInputRef}
          onChange={setDeviceImage}
        />
        <ImageCaptureButton
          file={labelImage}
          label="Label Photo (optional)"
          inputRef={labelInputRef}
          onChange={setLabelImage}
        />
      </ExceptionalImageCaptureRow>
    </ExceptionalSection>
  );
});

ExceptionalPartCreateStep.displayName = 'ExceptionalPartCreateStep';

export default ExceptionalPartCreateStep;
